import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import { StaysApiError } from './stays-client';
import type {
  StayCalendar,
  StayChip,
  StayFull,
  StayFullCreateResult,
  StayFullInput,
  StayFullPatch,
  StayOccupancy,
  StayOverview,
  StayStatus,
  StaysBlock,
  StaysEnvelope,
  StayTable,
} from '../interfaces/stays';

/**
 * LOBBERO STAYS — the full stay, owner blocks and the operations calendar
 * (ai-plans/stays plan S4; lobbero_be/src/stays/routes/{reservations,calendar,
 * blocks}.js). The DEFAULT axios instance, like stays-client.ts (cookies, the
 * CSRF header, the 401 → refresh → retry).
 *
 * Every failure is a StaysApiError (the server's code, words, field map and
 * extra body). Two domain refusals get their own class so a screen can branch
 * on them without string-matching:
 *   ReservationConflictError — STAY_OVERLAP, STAY_BLOCKED, BLOCK_OVERLAP,
 *     BLOCK_OVERLAPS_STAY (the body's `conflict` rides in `.conflict`);
 *   ReservationStaleError    — STAY_CHANGED (the server's current stay rides
 *     in `.current`).
 */

export class ReservationConflictError extends StaysApiError {
  get conflict(): Record<string, unknown> | null {
    return (this.extra.conflict as Record<string, unknown> | null | undefined) || null;
  }
}

export class ReservationStaleError extends StaysApiError {
  get current(): StayFull | null {
    return (this.extra.reservation as StayFull | undefined) || null;
  }
}

const CONFLICTS = new Set(['STAY_OVERLAP', 'STAY_BLOCKED', 'BLOCK_OVERLAP', 'BLOCK_OVERLAPS_STAY']);

const call = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) {
      const body = err.response.data || null;
      const code = body && typeof body.code === 'string' ? body.code : '';
      if (CONFLICTS.has(code)) throw new ReservationConflictError(err.response.status, body, fallback);
      if (code === 'STAY_CHANGED') throw new ReservationStaleError(err.response.status, body, fallback);
      throw new StaysApiError(err.response.status, body, fallback);
    }
    throw new StaysApiError(0, null, fallback);
  }
};

const listing = (id: string) => `${apiRoute}/stays/listings/${encodeURIComponent(id)}`;
const stay = (id: string) => `${apiRoute}/stays/reservations/${encodeURIComponent(id)}`;

export interface StayTableQuery {
  q?: string;
  stage?: StayChip;
  includeArchived?: boolean;
  limit?: number;
  cursor?: string | null;
}

// ── the stays table, one stay ──────────────────────────────────────────────
export const listListingStays = (listingId: string, query: StayTableQuery = {}): Promise<StayTable> =>
  call(
    axios.get(`${listing(listingId)}/reservations`, {
      params: {
        stage: query.stage || 'all',
        ...(query.q ? { q: query.q } : {}),
        ...(query.includeArchived ? { include_archived: '1' } : {}),
        ...(query.limit ? { limit: query.limit } : {}),
        ...(query.cursor ? { cursor: query.cursor } : {}),
      },
    }),
    'Could not load the stays.'
  );

export const getStay = (id: string): Promise<StayFull & { envelope: StaysEnvelope }> =>
  call(axios.get(stay(id)), 'Could not load this stay.');

// ── writes ─────────────────────────────────────────────────────────────────
export const createStay = (listingId: string, input: StayFullInput): Promise<StayFullCreateResult> =>
  call(axios.post(`${listing(listingId)}/reservations`, input), 'Could not save the stay.');

export const updateStay = (id: string, patch: StayFullPatch): Promise<StayFull> =>
  call(axios.patch(stay(id), patch), 'Could not save the stay.');

export const transitionStay = (id: string, to: StayStatus, version: number): Promise<StayFull> =>
  call(axios.post(`${stay(id)}/status`, { to, version }), 'Could not change the stay.');

export const archiveStay = (id: string, version: number): Promise<StayFull> =>
  call(axios.post(`${stay(id)}/archive`, { version }), 'Could not archive the stay.');

export const unarchiveStay = (id: string, version: number): Promise<StayFull> =>
  call(axios.post(`${stay(id)}/unarchive`, { version }), 'Could not undo the archive.');

export const setStayAccess = (id: string, revoked: boolean, version: number): Promise<StayFull> =>
  call(
    axios.post(`${stay(id)}/access`, { revoked, version }),
    revoked ? "Could not revoke the guest's access." : "Could not restore the guest's access."
  );

export const deleteStay = (id: string, version: number): Promise<void> =>
  call(axios.delete(stay(id), { params: { version } }), 'Could not delete the stay.').then(() => undefined);

// ── the Overview, the calendar, the picker's marks ─────────────────────────
export const getListingOverview = (listingId: string): Promise<StayOverview> =>
  call(axios.get(`${listing(listingId)}/overview`), 'Could not load this listing.');

export const getListingCalendar = (listingId: string, fromMonth?: string, months = 6): Promise<StayCalendar> =>
  call(
    axios.get(`${listing(listingId)}/calendar`, { params: { ...(fromMonth ? { from: fromMonth } : {}), months } }),
    'Could not load the calendar.'
  );

export const getListingOccupancy = (
  listingId: string,
  opts: { from?: string; to?: string; exclude?: string | null } = {}
): Promise<StayOccupancy> =>
  call(
    axios.get(`${listing(listingId)}/occupancy`, {
      params: {
        ...(opts.from ? { from: opts.from } : {}),
        ...(opts.to ? { to: opts.to } : {}),
        ...(opts.exclude ? { exclude: opts.exclude } : {}),
      },
    }),
    'Could not load the booked dates.'
  );

// ── owner blocks ───────────────────────────────────────────────────────────
export const listListingBlocks = (listingId: string, opts: { from?: string; to?: string } = {}): Promise<{ blocks: StaysBlock[] }> =>
  call(axios.get(`${listing(listingId)}/blocks`, { params: opts }), 'Could not load the closed dates.');

export const createBlock = (
  listingId: string,
  input: { start_date: string; end_date: string; note?: string }
): Promise<StaysBlock> => call(axios.post(`${listing(listingId)}/blocks`, input), 'Could not close these dates.');

export const deleteBlock = (blockId: string): Promise<void> =>
  call(axios.delete(`${apiRoute}/stays/blocks/${encodeURIComponent(blockId)}`), 'Could not re-open these dates.').then(
    () => undefined
  );

export const reopenDates = (
  listingId: string,
  range: { start_date: string; end_date: string }
): Promise<{ removed: number; ids: string[] }> =>
  call(axios.post(`${listing(listingId)}/blocks/reopen`, range), 'Could not re-open these dates.');
